import { Popover, PopoverContent, PopoverTrigger } from '@/components/atoms/Popover';
import { useConfig } from './ConfigProvider';
import { ChevronRight } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useAppSelector } from '@/redux/store';
import { CPLAT_API_ROUTES } from '@/api';
import { useSuspenseFetch } from '@/api';
import { StoreListData } from '@/types/api';
import { useMemo } from 'react';
import { SELECT_ALL_MARKET } from '@/pages/orders/_consts/constants';
import { getMarketKey } from '@/pages/orders/_utils/getMarketKey';
import NeedInviteManager from '@/pages/Market/NeedInviteManager';
import { NEED_INVITE_MANNAGER } from '@/pages/Market/_constants/const';
// import { TossPending } from '@/pages/Market/RegisteredMarketList';
import { ROUTES } from '@/routes';
export const ConnectedProvider = ({ children }: { children: React.ReactNode }) => {
  const user = useAppSelector((state) => state.user);

  const { registedMarketList } = useConfig();
  const navigate = useNavigate();

  const { data: registeredMarketRes } = useSuspenseFetch<StoreListData>(
    CPLAT_API_ROUTES.getStoreList,
    {
      method: 'POST',
      params: { cplatToken: user.CplatToken },
    },
  );

  const marketList = useMemo(() => {
    if (!registeredMarketRes?.data?.[0]?.StoreList) return [];
    return [SELECT_ALL_MARKET, ...registeredMarketRes.data[0]?.StoreList];
  }, [registeredMarketRes]);

  if (registedMarketList.length === 0) {
    return (
      <div
        tabIndex={0}
        role="button"
        aria-label="마켓 연동 페이지로 이동"
        onClick={() => navigate(ROUTES.market)}
        style={{ cursor: 'pointer' }}
      >
        {children}
      </div>
    );
  }

  return (
    <Popover>
      <PopoverTrigger asChild>{children}</PopoverTrigger>
      <PopoverContent className="max-h-[282px] w-[360px] overflow-y-auto bg-white px-0 py-[20px] ">
        <ul>
          {registedMarketList.map((market, idx) => {
            const hasComment = !!market.StoreComment;
            const isNeedInvite = market.StoreComment === NEED_INVITE_MANNAGER;
            const showOverlay = hasComment && !isNeedInvite;
            // const isTossPending = market.MarketNameEnglish === 'toss' && !market.IsTossConfirmed;
            return (
              <li
                key={market.MarketId + idx}
                className={`flex cursor-pointer items-center gap-[12px] border-b px-[20px] py-[12px] last:border-b-0 ${
                  hasComment ? 'hover:bg-gr-100' : 'cursor-default'
                }`}
                onClick={() => {
                  if (hasComment) {
                    navigate(ROUTES.market);
                  } else {
                    // marketList에서 해당 마켓을 찾는다
                    const found = marketList.find(
                      (m) =>
                        String(m.StoreId) === String(market.MarketId) &&
                        String(m.MarketName) === String(market.MarketName),
                    );
                    if (found) {
                      const selectKey = getMarketKey(found);
                      navigate(`${ROUTES.orderDetails}?orderTab=new-order&select=${selectKey}`);
                    } else {
                      // 못 찾으면 전체로 이동
                      navigate(
                        `${ROUTES.orderDetails}?orderTab=new-order&select=${getMarketKey(
                          marketList[0],
                        )}`,
                      );
                    }
                  }
                }}
                tabIndex={hasComment ? 0 : -1}
                aria-disabled={!hasComment}
              >
                <img
                  src={market.MarketIconUrl}
                  alt={market.MarketName}
                  className="h-[20px] w-[20px] rounded-full"
                  style={{ opacity: showOverlay ? 0.2 : 1 }}
                />
                <div className="flex flex-1 flex-col gap-[2px]">
                  <span
                    className={`truncate text-sm text-gr-800 Body5M14 ${
                      showOverlay ? 'text-opacity-20' : ''
                    }`}
                  >
                    {market.MarketAlias || market.MarketName || market.MarketId}
                  </span>
                  {hasComment &&
                    (isNeedInvite ? (
                      <NeedInviteManager />
                    ) : (
                      <span className="text-red-400 Body5M14">{market.StoreComment}</span>
                    ))}
                  {/* {isTossPending && <TossPending />} */}
                </div>
                {hasComment && <ChevronRight className="h-[24px] w-[24px] text-gr-600" />}
              </li>
            );
          })}
        </ul>
      </PopoverContent>
    </Popover>
  );
};
